import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { Button } from '../components/ui';
import { RefreshCw, Crown, KeyRound, Trash2 } from 'lucide-react';
import { DOCUMENT_TYPES_FOR_UPLOAD } from '../config/documentTypes';
import DrugTestingWaitlist from '../components/Admin/DrugTestingWaitlist';
import './founder-admin.css';

const FOUNDER_EMAIL = 'hello@tearratopia.com';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
}

function authJsonHeaders() {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  };
}

function docTypeLabel(code) {
  const d = DOCUMENT_TYPES_FOR_UPLOAD.find((x) => x.value === code);
  if (d) return d.label;
  if (!code || code === 'unknown') return 'Unknown type';
  return code.replace(/_/g, ' ');
}

function formatMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    n || 0,
  );
}

function driStatusLabel(score) {
  if (score == null || Number.isNaN(Number(score))) return 'No score';
  const s = Number(score);
  if (s >= 80) return 'Audit ready';
  if (s >= 60) return 'Needs attention';
  return 'Not audit ready';
}

function planPillClass(plan) {
  if (plan === 'fleet') return 'founder-admin__pill founder-admin__pill--fleet';
  if (plan === 'owner-operator') return 'founder-admin__pill founder-admin__pill--oo';
  return 'founder-admin__pill founder-admin__pill--free';
}

function planDisplay(plan) {
  if (plan === 'fleet') return 'Fleet';
  if (plan === 'owner-operator') return 'Owner-Operator';
  return 'Free';
}

function driClass(score) {
  if (score == null) return 'founder-admin__dri founder-admin__muted';
  const s = Number(score);
  if (s >= 80) return 'founder-admin__dri founder-admin__dri--high';
  if (s >= 60) return 'founder-admin__dri founder-admin__dri--mid';
  return 'founder-admin__dri founder-admin__dri--low';
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userHydrated, setUserHydrated] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [intel, setIntel] = useState(null);
  const [usersPayload, setUsersPayload] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [usersTableError, setUsersTableError] = useState('');
  const [usersTableSuccess, setUsersTableSuccess] = useState('');
  const [planMenuForId, setPlanMenuForId] = useState(null);
  const [resetPopover, setResetPopover] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (planMenuForId != null && !e.target.closest('.founder-admin__plan-wrap')) {
        setPlanMenuForId(null);
      }
      if (resetPopover && !e.target.closest('.founder-admin__reset-wrap')) {
        setResetPopover(null);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [planMenuForId, resetPopover]);

  useEffect(() => {
    if (!usersTableSuccess) return;
    const t = setTimeout(() => setUsersTableSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [usersTableSuccess]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw) {
      setUser(null);
      setUserHydrated(true);
      return;
    }
    try {
      setUser(JSON.parse(raw));
    } catch {
      setUser(null);
    }
    setUserHydrated(true);
  }, []);

  const allowed = user && user.email === FOUNDER_EMAIL;

  const fetchAll = useCallback(async () => {
    if (!allowed) return;
    setError('');
    const q = searchDebounced ? `&q=${encodeURIComponent(searchDebounced)}` : '';
    try {
      const [ov, ci, us] = await Promise.all([
        axios.get(`${API_URL}/api/fc/overview`, getAuthHeaders()),
        axios.get(`${API_URL}/api/fc/compliance-intelligence`, getAuthHeaders()),
        axios.get(`${API_URL}/api/fc/users?page=${page}&limit=20${q}`, getAuthHeaders()),
      ]);
      setOverview(ov.data);
      setIntel(ci.data);
      setUsersPayload(us.data);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        navigate('/dashboard', { replace: true });
        return;
      }
      setError(err.response?.data?.message || 'Failed to load admin data.');
    }
  }, [allowed, page, searchDebounced, navigate]);

  useEffect(() => {
    if (!userHydrated) return;
    if (!user) {
      setLoading(false);
      return;
    }
    if (user.email !== FOUNDER_EMAIL) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      await fetchAll();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, userHydrated, fetchAll]);

  const handleRefresh = async () => {
    if (!allowed) return;
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  const handleChangePlan = async (userId, plan) => {
    setUsersTableError('');
    setUsersTableSuccess('');
    setRowBusyId(userId);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/fc/users/${userId}/plan`,
        { plan },
        authJsonHeaders(),
      );
      setUsersPayload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          users: prev.users.map((x) => (x.id === userId ? { ...x, plan: data.plan } : x)),
        };
      });
      setPlanMenuForId(null);
      setUsersTableSuccess('Plan updated');
    } catch (err) {
      setUsersTableSuccess('');
      setUsersTableError(err.response?.data?.message || err.message || 'Failed to update plan');
    } finally {
      setRowBusyId(null);
    }
  };

  const handleSendResetEmail = async (userId) => {
    setUsersTableError('');
    setUsersTableSuccess('');
    setRowBusyId(userId);
    try {
      await axios.post(`${API_URL}/api/fc/users/${userId}/reset-password`, {}, getAuthHeaders());
      setResetPopover(null);
      setUsersTableSuccess('Reset email sent');
    } catch (err) {
      setUsersTableSuccess('');
      setUsersTableError(err.response?.data?.message || err.message || 'Failed to send reset email');
    } finally {
      setRowBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal) return;
    setUsersTableError('');
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/fc/users/${deleteModal.id}`, getAuthHeaders());
      const removedId = deleteModal.id;
      setDeleteModal(null);
      setUsersPayload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          users: prev.users.filter((x) => x.id !== removedId),
          total: Math.max(0, (prev.total ?? 0) - 1),
        };
      });
      setUsersTableSuccess('User deleted successfully');
    } catch (err) {
      setUsersTableSuccess('');
      setUsersTableError(err.response?.data?.message || err.message || 'Failed to delete user');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const totalUsers = overview?.users?.total ?? 0;
  const sub = overview?.subscriptions;
  const pct = (n) => (totalUsers ? Math.round((n / totalUsers) * 1000) / 10 : 0);

  const dist = intel?.scoreDistribution;
  const distTotal =
    (dist?.notAuditReady ?? 0) + (dist?.needsAttention ?? 0) + (dist?.auditReady ?? 0) || 1;
  const distPct = (n) => Math.round(((n || 0) / distTotal) * 1000) / 10;

  const adoption = intel?.featureAdoption;

  if (!localStorage.getItem('token')) {
    return <Navigate to="/login" replace />;
  }

  if (!userHydrated) {
    return (
      <div className="founder-admin page-container">
        <p className="founder-admin__muted">Loading…</p>
      </div>
    );
  }

  if (user && user.email !== FOUNDER_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  if (forbidden) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading && !overview) {
    return (
      <div className="founder-admin page-container">
        <p className="founder-admin__muted">Loading founder dashboard…</p>
      </div>
    );
  }

  return (
    <div className="founder-admin page-container">
      <div className="founder-admin__header">
        <div className="founder-admin__title-block">
          <p className="founder-admin__admin-label">Admin only</p>
          <h1 className="founder-admin__title">Founder Dashboard</h1>
          <p className="founder-admin__subtitle">Sovrynn platform intelligence</p>
        </div>
        <div className="founder-admin__actions">
          <Button variant="secondary" icon={RefreshCw} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {error ? <div className="founder-admin__error">{error}</div> : null}

      {/* Section 1 — key metrics */}
      <div className="founder-admin__grid-4">
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{totalUsers.toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Total users</div>
          <div className="founder-admin__stat-sub founder-admin__stat-sub--green">
            +{overview?.users?.newThisWeek ?? 0} this week
          </div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{formatMoney(sub?.mrr ?? 0)}</div>
          <div className="founder-admin__stat-sub">MRR (est.)</div>
          <div className="founder-admin__stat-sub">
            {sub?.ownerOperator ?? 0} Owner-Op · {sub?.fleet ?? 0} Fleet
          </div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">
            {overview?.compliance?.averageDriScore != null ? `${overview.compliance.averageDriScore}` : '—'}
            <span className="founder-admin__muted" style={{ fontSize: '1rem', fontWeight: 500 }}>
              /100
            </span>
          </div>
          <div className="founder-admin__stat-sub">Average DRI score</div>
          <div className="founder-admin__stat-sub">{driStatusLabel(overview?.compliance?.averageDriScore)}</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{(overview?.users?.activeThisMonth ?? 0).toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Active this month</div>
          <div className="founder-admin__stat-sub">of {totalUsers.toLocaleString()} total users</div>
        </div>
      </div>

      {/* Section 2 — subscriptions */}
      <h2 className="founder-admin__section-title">Subscription breakdown</h2>
      <p className="founder-admin__section-sub">Share of all accounts by tier</p>
      <div className="founder-admin__grid-3">
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{sub?.free ?? 0}</div>
          <div className="founder-admin__stat-sub">Free</div>
          <div className="founder-admin__stat-sub">{pct(sub?.free ?? 0)}% of total</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{sub?.ownerOperator ?? 0}</div>
          <div className="founder-admin__stat-sub">Owner-Operator</div>
          <div className="founder-admin__stat-sub">{pct(sub?.ownerOperator ?? 0)}% of total</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{sub?.fleet ?? 0}</div>
          <div className="founder-admin__stat-sub">Fleet</div>
          <div className="founder-admin__stat-sub">{pct(sub?.fleet ?? 0)}% of total</div>
        </div>
      </div>

      {/* Section 3 — data moat */}
      <h2 className="founder-admin__section-title">Your data moat</h2>
      <p className="founder-admin__section-sub">Aggregate compliance intelligence across all operators</p>
      <div className="founder-admin__moat">
        <div className="founder-admin__card">
          <h3 className="founder-admin__section-title" style={{ fontSize: '1rem' }}>
            Score distribution
          </h3>
          <div className="founder-admin__bar-row">
            <div className="founder-admin__bar-label">
              <span>Audit ready</span>
              <span>
                {dist?.auditReady ?? 0} users ({distPct(dist?.auditReady)}%)
              </span>
            </div>
            <div className="founder-admin__bar-track">
              <div
                className="founder-admin__bar-fill founder-admin__bar-fill--green"
                style={{ width: `${distPct(dist?.auditReady)}%` }}
              />
            </div>
          </div>
          <div className="founder-admin__bar-row">
            <div className="founder-admin__bar-label">
              <span>Needs attention</span>
              <span>
                {dist?.needsAttention ?? 0} users ({distPct(dist?.needsAttention)}%)
              </span>
            </div>
            <div className="founder-admin__bar-track">
              <div
                className="founder-admin__bar-fill founder-admin__bar-fill--amber"
                style={{ width: `${distPct(dist?.needsAttention)}%` }}
              />
            </div>
          </div>
          <div className="founder-admin__bar-row">
            <div className="founder-admin__bar-label">
              <span>Not audit ready</span>
              <span>
                {dist?.notAuditReady ?? 0} users ({distPct(dist?.notAuditReady)}%)
              </span>
            </div>
            <div className="founder-admin__bar-track">
              <div
                className="founder-admin__bar-fill founder-admin__bar-fill--red"
                style={{ width: `${distPct(dist?.notAuditReady)}%` }}
              />
            </div>
          </div>
          <p className="founder-admin__stat-sub" style={{ marginTop: 12 }}>
            Trend: {intel?.scoreTrend?.trend ?? 'stable'} (this week avg {intel?.scoreTrend?.averageScoreThisWeek ?? 0}{' '}
            vs last week {intel?.scoreTrend?.averageScoreLastWeek ?? 0})
          </p>
        </div>
        <div className="founder-admin__card">
          <h3 className="founder-admin__section-title" style={{ fontSize: '1rem' }}>
            Most common gaps
          </h3>
          <p className="founder-admin__section-sub" style={{ marginTop: 0 }}>
            Top compliance gaps across your user base
          </p>
          <ul className="founder-admin__gap-list">
            {(intel?.commonMissingDocuments || []).map((row) => (
              <li key={row.documentType}>
                {docTypeLabel(row.documentType)} — {row.count} events
              </li>
            ))}
            {(!intel?.commonMissingDocuments || intel.commonMissingDocuments.length === 0) && (
              <li className="founder-admin__muted">No document expiry events yet.</li>
            )}
          </ul>
          {(intel?.commonAlerts || []).length > 0 && (
            <>
              <h4 className="founder-admin__section-title" style={{ fontSize: '0.95rem', marginTop: 20 }}>
                Common alerts (active insights)
              </h4>
              <ul className="founder-admin__gap-list">
                {(intel?.commonAlerts || []).map((row) => (
                  <li key={row.title}>
                    {row.title} — {row.count}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Section 4 — adoption */}
      <h2 className="founder-admin__section-title">Feature adoption</h2>
      <p className="founder-admin__section-sub">Share of users who used each capability</p>
      <div className="founder-admin__card" style={{ marginBottom: 32 }}>
        {[
          { label: 'Compliance Passport generated', v: adoption?.passportGenerationRate ?? 0 },
          { label: 'Rynn conversation started', v: adoption?.rynnUsageRate ?? 0 },
          { label: 'Audit Packet downloaded', v: adoption?.auditPacketDownloadRate ?? 0 },
          { label: 'Audit Readiness checklist viewed', v: adoption?.checklistCompletionRate ?? 0 },
        ].map((row) => (
          <div key={row.label} className="founder-admin__adoption-row">
            <div className="founder-admin__adoption-head">
              <span>{row.label}</span>
              <span>{row.v}%</span>
            </div>
            <div className="founder-admin__bar-track">
              <div
                className="founder-admin__bar-fill founder-admin__bar-fill--amber"
                style={{ width: `${Math.min(100, row.v)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <DrugTestingWaitlist />

      {/* Section 5 — users */}
      <h2 className="founder-admin__section-title">All users</h2>
      <input
        type="search"
        className="founder-admin__search"
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search users"
      />
      {usersTableError ? (
        <div className="founder-admin__table-msg founder-admin__table-msg--error" role="alert">
          {usersTableError}
        </div>
      ) : null}
      {usersTableSuccess ? (
        <div className="founder-admin__table-msg founder-admin__table-msg--success" role="status">
          {usersTableSuccess}
        </div>
      ) : null}
      <div className="founder-admin__table-wrap">
        <table className="founder-admin__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Plan</th>
              <th>DRI score</th>
              <th>Joined</th>
              <th>Has passport</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(usersPayload?.users || []).map((u) => (
              <tr key={u.id}>
                <td>{u.name || '—'}</td>
                <td>{u.email}</td>
                <td>{u.phone || '—'}</td>
                <td>
                  <span className={planPillClass(u.plan)}>{planDisplay(u.plan)}</span>
                </td>
                <td className={driClass(u.driScore)}>{u.driScore != null ? u.driScore : '—'}</td>
                <td>
                  {u.createdAt
                    ? new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </td>
                <td>{u.hasPassport ? 'Yes' : 'No'}</td>
                <td className="founder-admin__actions-cell">
                  <div className="founder-admin__action-row">
                    <div
                      className="founder-admin__plan-wrap"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="founder-admin__icon-btn founder-admin__icon-btn--amber"
                        aria-label="Change plan"
                        aria-expanded={planMenuForId === u.id}
                        disabled={rowBusyId === u.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setResetPopover(null);
                          setPlanMenuForId((cur) => (cur === u.id ? null : u.id));
                        }}
                      >
                        <Crown size={17} strokeWidth={2} />
                      </button>
                      {planMenuForId === u.id ? (
                        <div className="founder-admin__plan-menu" role="menu">
                          {['free', 'owner-operator', 'fleet'].map((p) => (
                            <button
                              key={p}
                              type="button"
                              role="menuitem"
                              className="founder-admin__plan-menu-item"
                              disabled={rowBusyId === u.id || u.plan === p}
                              onClick={() => handleChangePlan(u.id, p)}
                            >
                              {planDisplay(p)}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div
                      className="founder-admin__reset-wrap"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="founder-admin__icon-btn founder-admin__icon-btn--amber"
                        aria-label="Send password reset email"
                        disabled={rowBusyId === u.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPlanMenuForId(null);
                          setResetPopover((cur) => (cur?.id === u.id ? null : { id: u.id, email: u.email }));
                        }}
                      >
                        <KeyRound size={17} strokeWidth={2} />
                      </button>
                      {resetPopover?.id === u.id ? (
                        <div className="founder-admin__reset-popover" role="dialog" aria-label="Confirm password reset">
                          <p className="founder-admin__reset-popover-text">
                            Send password reset email to {u.email}?
                          </p>
                          <div className="founder-admin__reset-popover-actions">
                            <button type="button" className="founder-admin__btn-text" onClick={() => setResetPopover(null)}>
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="founder-admin__btn-primary-sm"
                              disabled={rowBusyId === u.id}
                              onClick={() => handleSendResetEmail(u.id)}
                            >
                              Send Reset Email
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {u.email !== FOUNDER_EMAIL ? (
                      <button
                        type="button"
                        className="founder-admin__icon-btn founder-admin__icon-btn--danger"
                        aria-label="Delete user"
                        disabled={rowBusyId === u.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPlanMenuForId(null);
                          setResetPopover(null);
                          setUsersTableError('');
                          setDeleteModal({ id: u.id, email: u.email, name: u.name || '—' });
                        }}
                      >
                        <Trash2 size={17} strokeWidth={2} />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="founder-admin__pagination">
        <span>
          Page {usersPayload?.page ?? page} of {usersPayload?.pages ?? 1} ({usersPayload?.total ?? 0} users)
        </span>
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={!usersPayload || page >= (usersPayload.pages ?? 1)}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>

      {/* Section 6 — platform */}
      <h2 className="founder-admin__section-title" style={{ marginTop: 32 }}>
        Platform stats
      </h2>
      <div className="founder-admin__grid-4-sm">
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{(overview?.platform?.totalDocumentsUploaded ?? 0).toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Documents uploaded</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{(overview?.platform?.totalVehicles ?? 0).toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Vehicles tracked</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{(overview?.platform?.totalNotificationsSent ?? 0).toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Notifications sent</div>
        </div>
        <div className="founder-admin__card">
          <div className="founder-admin__stat-num">{(overview?.platform?.totalComplianceEvents ?? 0).toLocaleString()}</div>
          <div className="founder-admin__stat-sub">Compliance events</div>
        </div>
      </div>

      {deleteModal ? (
        <div
          className="founder-admin__modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteSubmitting) setDeleteModal(null);
          }}
        >
          <div
            className="founder-admin__modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="founder-admin-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="founder-admin-delete-title" className="founder-admin__modal-title">
              Delete User
            </h2>
            <p className="founder-admin__modal-body">
              This will permanently delete {deleteModal.name} ({deleteModal.email}) and all their data including documents,
              vehicles, compliance history, and cancel their Stripe subscription if active. This cannot be undone.
            </p>
            <div className="founder-admin__modal-actions">
              <button
                type="button"
                className="founder-admin__modal-btn founder-admin__modal-btn--secondary"
                disabled={deleteSubmitting}
                onClick={() => setDeleteModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="founder-admin__modal-btn founder-admin__modal-btn--delete"
                disabled={deleteSubmitting}
                onClick={handleConfirmDelete}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
